import { z } from "zod";

/**
 * The one definition of an approval email's link: the email builds it here and
 * the approvals page parses it with the same schema, so neither side can rename
 * a key alone. The links only navigate; the approver still clicks the verdict.
 */
const AGENT_APPROVALS_PATH = "/dashboard/agents/approvals";

/**
 * The approvals page's search. A malformed value is dropped rather than failing
 * the page, so a mangled link still opens the queue.
 */
export const approvalLinkSearchSchema = z.object({
	agent: z.uuid().optional().catch(undefined),
	run: z.uuid().optional().catch(undefined),
	call: z.string().min(1).max(200).optional().catch(undefined),
	verdict: z.enum(["approve", "decline"]).optional().catch(undefined),
});

export type ApprovalLinkSearch = z.infer<typeof approvalLinkSearchSchema>;

export type ApprovalVerdict = NonNullable<ApprovalLinkSearch["verdict"]>;

/** The run page an outcome email links to. */
export const runPageUrl = (
	appUrl: string,
	run: { agentId: string; runId: string },
): string =>
	`${appUrl}/dashboard/agents/${encodeURIComponent(run.agentId)}/runs/${encodeURIComponent(run.runId)}`;

export interface ApprovalLinks {
	/** The approvals page focused on the run, nothing preselected. */
	review: string;
	/** Present only when exactly one call is pending, so a verdict names it. */
	verdicts: { approve: string; decline: string } | null;
}

const approvalsPageUrl = (
	appUrl: string,
	search: ApprovalLinkSearch,
): string => {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(search)) {
		if (value !== undefined) {
			params.set(key, value);
		}
	}
	return `${appUrl}${AGENT_APPROVALS_PATH}?${params.toString()}`;
};

export function buildApprovalLinks(
	appUrl: string,
	run: { agentId: string; runId: string; callIds: readonly string[] },
): ApprovalLinks {
	const [callId] = run.callIds;
	const verdictUrl = (call: string, verdict: ApprovalVerdict): string =>
		approvalsPageUrl(appUrl, {
			agent: run.agentId,
			run: run.runId,
			call,
			verdict,
		});
	return {
		review: approvalsPageUrl(appUrl, { agent: run.agentId, run: run.runId }),
		verdicts:
			run.callIds.length === 1 && callId !== undefined
				? {
						approve: verdictUrl(callId, "approve"),
						decline: verdictUrl(callId, "decline"),
					}
				: null,
	};
}
